import React, { useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import "./ContactForm.css";
import Header from "../common/Header";
import Footer from "../common/Footer";
import PageWrapper from "../common/PageWrapper";
import { contactAPI } from "../../api/endpoints/contact";

const Contact = ({ menuState }) => {
  const { isMenuOpen, setIsMenuOpen, onMenuToggle } = menuState;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    concession: "",
    SIRET: "",
    codePostal: "",
    ville: "",
    KBIS: null,
    message: "",
    consent: false
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, files, checked} = e.target;

    if (type === "file") {
      const file = files[0];
      setFormData({ ...formData, [name]: file });

      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setSelectedFile(file);
        };
        reader.readAsDataURL(file);
      } else {
        setSelectedFile(null);
      }
    } else if (type === "checkbox") {
      // Gestion des cases à cocher
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    if (!formData.consent) {
      setError("Vous devez accepter d'être recontacté pour soumettre le formulaire");
      setIsLoading(false);
      return;
    }
    
    try {
      const formDataToSend = new FormData();
      
      // Log des données avant envoi
      console.log('Données du formulaire:', formData);
      console.log('Fichier sélectionné:', selectedFile);

      // Ajouter les champs un par un
      formDataToSend.append('firstName', formData.firstName);
      formDataToSend.append('lastName', formData.lastName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('concession', formData.concession);
      formDataToSend.append('SIRET', formData.SIRET);
      formDataToSend.append('codePostal', formData.codePostal);
      formDataToSend.append('ville', formData.ville);
      formDataToSend.append('message', formData.message);

      // Ajouter le fichier s'il existe
      if (selectedFile) {
        console.log('Ajout du fichier au FormData:', selectedFile.name);
        formDataToSend.append('KBIS', selectedFile);
      }

      // Vérifier le contenu du FormData avant envoi
      console.log('Contenu du FormData:');
      for (let pair of formDataToSend.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }

      const response = await contactAPI.sendContactForm(formDataToSend);
      console.log('Réponse du serveur:', response);

      setSuccess("Votre formulaire a été soumis avec succès.");
      
      // Réinitialisation du formulaire
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        concession: "",
        SIRET: "",
        codePostal: "",
        ville: "",
        KBIS: null,
        message: "",
        consent: false
      });
      setSelectedFile(null);
    } catch (error) {
      console.error("Erreur détaillée:", error);
      setError(error.response?.data?.error || "Une erreur est survenue lors de l'envoi du message.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB max
        alert("Le fichier est trop volumineux. Taille maximum : 5MB");
        return;
      }
      setSelectedFile(file);
      setFormData(prev => ({ ...prev, KBIS: file }));
    }
  };

  return (
    <div className="landing-wrapper">
      <Header
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        onMenuToggle={onMenuToggle}
      />
      <PageWrapper isMenuOpen={isMenuOpen}>
        <main className="contact-content">
          <div className="contact-container">
            <div className="contact-title-container">
              <h2>Contactez nous !</h2>
              <p>
                <span>
                  VéhiDoc, l&apos;interface qui vous simplifiera la vie !
                </span>
              </p>
              <div className="contact-info">
                <p>
                  <FontAwesomeIcon icon={faPhone} className="contact-icon" />
                  <span>
                    <a href="tel:0781283033">07 81 28 30 33</a>
                  </span>
                </p>
                <p>
                  <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
                  <span>
                    <a href="mailto:contact@vehidoc.fr">contact@vehidoc.fr</a>
                  </span>
                </p>
              </div>
              <p>
                Pour toutes les concessions, marques et véhicules
              </p>
            </div>
            <div className="contact-form-container">
              <div className="contact-form-header">
                <h3>Optimisez votre temps de travail !</h3>
                <p>Complétez le formulaire, nous vous recontacterons.</p>
              </div>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Nom*</label>
                  <input
                    type="text"
                    name="lastName"
                    placeholder=""
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Prénom*</label>
                  <input
                    type="text"
                    name="firstName"
                    placeholder=""
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email*</label>
                  <input
                    type="email"
                    name="email"
                    placeholder=""
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone*</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder=""
                    value={formData.phone}
                    onChange={handleChange}
                    pattern="[0-9]{10}"
                    maxLength="10"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Concession</label>
                  <input
                    type="text"
                    name="concession"
                    placeholder=""
                    value={formData.concession}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>SIRET</label>
                  <input
                    type="text"
                    name="SIRET"
                    placeholder=""
                    value={formData.SIRET}
                    onChange={handleChange}
                    pattern="[0-9]{14}"
                    maxLength="14"
                  />
                </div>
                <div className="form-group">
                  <label>Code postal</label>
                  <input
                    type="text"
                    name="codePostal"
                    placeholder=""
                    value={formData.codePostal}
                    onChange={handleChange}
                    pattern="[0-9]{5}"
                    maxLength="5"
                  />
                </div>
                <div className="form-group">
                  <label>Ville</label>
                  <input
                    type="text"
                    name="ville"
                    placeholder=""
                    value={formData.ville}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Extrait KBIS (- 6mois)</label>
                  <div className="file-input-container">
                    <label className="custom-file-button">
                      <input
                        type="file"
                        name="KBIS"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                      />
                      <span className="file-label">
                        {selectedFile ? selectedFile.name : ""}
                      </span>
                      <div className="download-button">
                        <FontAwesomeIcon
                          icon={faDownload}
                          className="download-icon"
                        />
                        <span>Télécharger</span>
                      </div>
                    </label>
                  </div>
                </div>
                <div className="textarea-group">
                  <label>Message :</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                  />
                </div>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="consent"
                      checked={formData.consent}
                      onChange={handleChange}
                      required
                    />
                    J&apos;accepte que mes données personnelles soient traitées
                    selon la politique de confidentialité du site.
                  </label>
                </div>
                <div className="button-container">
                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? "Envoi en cours..." : "Etre recontacté"}
                  </button>
                </div>
              </form>
              {error && (
                <p className="error-message">{error}</p>
              )}
              {success && (
                <p className="success-message">{success}</p>
              )}
            </div>
          </div>
        </main>
      </PageWrapper>
      <Footer />
    </div>
  );
};

Contact.propTypes = {
  menuState: PropTypes.shape({
    isMenuOpen: PropTypes.bool.isRequired,
    setIsMenuOpen: PropTypes.func.isRequired,
    onMenuToggle: PropTypes.func.isRequired,
  }).isRequired,
};

export default Contact;
